import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class FormulasService {
  // Converts degrees to radians
  toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Converts radians to degrees
  toDegrees(radians: number): number {
    return radians * (180 / Math.PI);
  }

  // Calculates the dog leg angle (β) in radians
  getDogLegAngle(INC1: number, AZ1: number, INC2: number, AZ2: number): number {
    // β = arccos(cos(I_2-I_1) - sin(I_1)*sin(I_2)*(1-cos(A_2-A_1)))
    return Math.acos(
      Math.cos(INC2 - INC1) - Math.sin(INC1) * Math.sin(INC2) * (1 - Math.cos(AZ2 - AZ1)),
    );
  }

  // Calculates the trajectory path factor (RF)
  getTrajectoryPath(β: number): number {
    // RF = 2/β * tan(β/2)
    if (Math.abs(β) < 1e-7) {
      return 1.0;
    }
    return (2 / β) * Math.tan(β / 2);
  }

  // Calculates the East-West (X) coordinate
  getEastWestCoordinate(
    MD1: number,
    MD2: number,
    INC1: number,
    INC2: number,
    AZ1: number,
    AZ2: number,
    RF: number,
    x1: number,
  ): number {
    // X = ((MD_2-MD_1)/2)*(sin(I_1)*sin(A_1)+sin(I_2)*sin(A_2))*RF + x1
    return (
      ((MD2 - MD1) / 2) * (Math.sin(INC1) * Math.sin(AZ1) + Math.sin(INC2) * Math.sin(AZ2)) * RF +
      x1
    );
  }

  // Calculates the North-South (Y) coordinate
  getNorthSouthCoordinate(
    MD1: number,
    MD2: number,
    INC1: number,
    INC2: number,
    AZ1: number,
    AZ2: number,
    RF: number,
    y1: number,
  ): number {
    // Y = ((MD_2-MD_1)/2)*(sin(I_1)*cos(A_1)+sin(I_2)*cos(A_2))*RF + y1
    return (
      ((MD2 - MD1) / 2) * (Math.sin(INC1) * Math.cos(AZ1) + Math.sin(INC2) * Math.cos(AZ2)) * RF +
      y1
    );
  }

  // Calculates the True Vertical (Z) coordinate
  getTrueVerticalCoordinate(
    MD1: number,
    MD2: number,
    INC1: number,
    INC2: number,
    RF: number,
    z1: number,
  ): number {
    // Z = ((MD_2-MD_1)/2)*(cos(I_1)+cos(I_2))*RF + z1
    return ((MD2 - MD1) / 2) * (Math.cos(INC1) + Math.cos(INC2)) * RF + z1;
  }

  // Calculates the Dog Leg Severity (DLS) in degrees per 100 units of measured depth
  getDogLegSeverity(β: number, MD1: number, MD2: number): number {
    // DLS = (β / (MD_2 - MD_1)) × 100
    if (MD2 === MD1) {
      return 0;
    }
    return this.toDegrees(β / Math.abs(MD2 - MD1)) * 100;
  }

  // Calculates the Side Loading (SL)
  getSideLoading(DLS: number, MaxLoad: number): number {
    // SL = MaxLoad * sin(DLS/4)
    return MaxLoad * Math.sin(this.toRadians(DLS) / 4);
  }
}
